import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { Home, Ticket, PlusCircle, User, Menu, X, PanelLeftClose, PanelLeftOpen, HelpCircle } from 'lucide-react';
import { useAuthStore } from '../../stores/auth-store';
import { useThemeStore } from '../../stores/theme-store';
import { useTutorialStore } from '../../stores/tutorial-store';
import { NotificationDropdown } from '../ui/NotificationDropdown';
import { ThemeToggle } from '../ui/ThemeToggle';
import { TutorialProvider } from '../ui/TutorialProvider';
import { cn } from '../../lib/utils';
import { useState, useEffect } from 'react';
import { useActivityHeartbeat } from '../../hooks/use-activity';
import { useLogoutAction } from '../../hooks/use-auth';

const navItems = [
  { to: '/', icon: Home, label: 'Acasă', tutorial: 'dashboard' },
  { to: '/tickets', icon: Ticket, label: 'Tichete', tutorial: 'nav-tickets' },
  { to: '/tickets/new', icon: PlusCircle, label: 'Tichet Nou', tutorial: 'nav-new-ticket' },
  { to: '/profile', icon: User, label: 'Profil', tutorial: 'nav-profile' },
];

export function ClientLayout() {
  const user = useAuthStore((s) => s.user);
  const logout = useLogoutAction();
  const dark = useThemeStore((s) => s.dark);
  const startTutorial = useTutorialStore((s) => s.start);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('helpdesk_sidebar_collapsed') === 'true'; } catch { return false; }
  });
  const location = useLocation();
  useActivityHeartbeat();

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('helpdesk_sidebar_collapsed', String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const sidebarContent = (collapsed: boolean) => (
    <>
      <div
        className={cn(
          'border-b border-[color:var(--surface-line)] py-4',
          collapsed ? 'flex min-h-20 items-center justify-center px-2' : 'relative min-h-[8.75rem] px-5',
        )}
      >
        <Link to="/" title="Acasă" className="block">
          {collapsed ? (
            <img
              alt="Encorsa"
              src={dark ? '/logo_encorsa_ALB_bulina.png' : '/logo_encorsa_COLOR_bulina.png'}
              className="h-10 w-10 transition-all duration-200"
            />
          ) : (
            <div className="min-w-0 pr-10">
              <p className="app-kicker">Encorsa support</p>
              <p className="mt-1 text-sm font-semibold text-[color:var(--ink-strong)]">Portal client</p>
              <img
                alt="Logo"
                src={dark ? '/logo_encorsa_ALB_mic.png' : '/logo_encorsa_COLOR_mic.png'}
                className="mt-4 h-8 w-auto transition-all duration-200"
              />
            </div>
          )}
        </Link>
        <button className="absolute right-4 top-4 md:hidden" onClick={() => setMobileMenuOpen(false)}><X size={20} className="text-[color:var(--ink-muted)]" /></button>
      </div>
      <nav className="flex-1 space-y-1.5 p-3.5">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            data-tutorial={item.tutorial}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-[1.15rem] border px-3.5 py-3 text-sm font-medium transition-all duration-200',
                collapsed && 'justify-center px-0',
                isActive
                  ? 'border-[color:var(--surface-line)] bg-[var(--surface-panel-strong)] text-primary shadow-[var(--shadow-soft)]'
                  : 'border-transparent text-[color:var(--ink-muted)] hover:border-[color:var(--surface-line)] hover:bg-[var(--surface-panel-strong)] hover:text-[color:var(--ink-strong)]',
              )
            }
          >
            <item.icon size={20} />
            {!collapsed && item.label}
          </NavLink>
        ))}
      </nav>

      {/* Collapse toggle at the bottom (desktop only) */}
      <div className="hidden border-t border-[color:var(--surface-line)] p-3 md:flex">
        <button
          onClick={toggleSidebar}
          className={cn(
            'flex w-full items-center gap-2 rounded-[1.15rem] border border-transparent px-3 py-2.5 text-sm font-medium text-[color:var(--ink-muted)] transition-all duration-200 hover:border-[color:var(--surface-line)] hover:bg-[var(--surface-panel-strong)] hover:text-[color:var(--ink-strong)]',
            collapsed && 'justify-center px-0',
          )}
          title={collapsed ? 'Extinde meniul' : 'Restrânge meniul'}
        >
          {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          {!collapsed && 'Restrânge'}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <aside className={cn(
        'hidden shrink-0 flex-col border-r border-[color:var(--surface-line)] bg-[var(--surface-panel)] transition-all duration-300 md:flex',
        sidebarCollapsed ? 'w-[68px]' : 'w-[260px]',
      )}>
        {sidebarContent(sidebarCollapsed)}
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} />
          <aside className="relative z-50 flex h-full w-[260px] flex-col bg-[var(--surface-panel)]">
            {sidebarContent(false)}
          </aside>
        </div>
      )}

      {/* Main area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 shrink-0 items-center gap-4 border-b border-[color:var(--surface-line)] bg-[var(--surface-panel)] px-4 md:px-6">
          <button className="md:hidden" onClick={() => setMobileMenuOpen(true)}>
            <Menu size={22} className="text-[color:var(--ink-muted)]" />
          </button>
          <div className="flex-1" />
          <button
            onClick={startTutorial}
            className="rounded-full border border-transparent p-2 text-[color:var(--ink-soft)] transition-all duration-200 hover:border-[color:var(--surface-line)] hover:bg-[var(--surface-panel-strong)] hover:text-[color:var(--ink-strong)]"
            title="Tutorial"
          >
            <HelpCircle size={20} />
          </button>
          <div data-tutorial="theme-toggle"><ThemeToggle /></div>
          <div data-tutorial="notifications"><NotificationDropdown /></div>
          <div className="flex items-center gap-2">
            <span className="hidden text-sm font-medium text-[color:var(--ink-strong)] sm:inline">{user?.name}</span>
            <button
              onClick={logout}
              className="text-xs text-[color:var(--ink-soft)] hover:text-[color:var(--ink-strong)]"
            >
              Ieșire
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-transparent p-4 md:p-6 lg:p-8">
          <div className="mx-auto w-full max-w-[1560px]">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Tutorial tour */}
      <TutorialProvider />
    </div>
  );
}
